import { IChingCastMode, IChingCoinFace, IChingLineValue } from '../core/iching.models';

/**
 * Concept van een lopende raadpleging op de Classic I Ching-pagina: vraag,
 * worp in uitvoering en eigen aantekeningen, zodat een herlaad of een
 * uitstapje naar een andere pagina niets laat verloren gaan.
 */
export interface IChingDraft {
  readonly question: string;
  readonly questionKind: 'question' | 'situation';
  readonly castMode: IChingCastMode;
  /** Gegooide lijnen onder→boven; korter dan 6 = worp bezig. */
  readonly lines: readonly IChingLineValue[];
  readonly coinFaces: ReadonlyArray<readonly [IChingCoinFace, IChingCoinFace, IChingCoinFace]>;
  readonly keywords: readonly string[];
  readonly notes: string;
  readonly doList: readonly string[];
  readonly dontList: readonly string[];
  readonly relationMode: boolean;
  readonly nameRevealed: boolean;
  /**
   * Half ingevulde handmatige worp (index 0 = lijn 1, `undefined` = nog niet gekozen), zodat
   * gedeeltelijke invoer een herlaad overleeft. Vervalt zodra de lijnen zelf wijzigen.
   */
  readonly manualLines?: readonly (IChingLineValue | undefined)[];
  /**
   * Het moment waarop de raadpleging gaat (ISO, UTC): op de pagina het ogenblik waarop de zesde
   * lijn viel, vanuit een moment of de transits het gekozen tijdstip. Gaat mee naar het dagboek en
   * plaatst de raadpleging op de levenstijdlijn.
   */
  readonly consultedAt?: string;
  /**
   * Het tijdstip is bewust gekozen (door een moment, de transits of de gebruiker) en blijft staan
   * bij een nieuwe worp; zonder pin krijgt een nieuwe worp het ogenblik van de zesde lijn.
   */
  readonly consultedAtPinned?: boolean;
  /** Chart waarvan deze raadpleging een moment is (levenstijdlijn); leeg = de eigen chart. */
  readonly linkedChartId?: string;
  /** Id van de bewaarde of heropende raadpleging: bewaren werkt die bij in plaats van een nieuwe te maken. */
  readonly savedId?: string;
  /** Veld dat een update volledig vervangt; meegeven zodat een bewaarbeurt het niet wist. */
  readonly savedRevisitedOn?: string;
}

export const EMPTY_ICHING_DRAFT: IChingDraft = {
  question: '',
  questionKind: 'question',
  castMode: 'coins',
  lines: [],
  coinFaces: [],
  keywords: [],
  notes: '',
  doList: [],
  dontList: [],
  relationMode: false,
  nameRevealed: false,
};

/**
 * localStorage is hier bewust acceptabel (in tegenstelling tot UI-keuzes, die
 * via de account-sync lopen): een concept leeft kort, het definitieve dagboek
 * staat in de backend, en de app wist localStorage toch bij elke update.
 */
export const ICHING_DRAFT_STORAGE_KEY = 'egitura.iching.draft';

/** Laadt het bewaarde concept; ontbrekend of onleesbaar → leeg concept. */
export function loadIChingDraft(): IChingDraft {
  try {
    const raw = localStorage.getItem(ICHING_DRAFT_STORAGE_KEY);
    if (raw == null) {
      return EMPTY_ICHING_DRAFT;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return EMPTY_ICHING_DRAFT;
    }
    // Merge over het lege concept: velden die later bijkomen krijgen zo een default.
    const { savedLinkedChartId, ...stored } = parsed as Partial<IChingDraft> & { savedLinkedChartId?: string };
    const draft: IChingDraft = { ...EMPTY_ICHING_DRAFT, ...stored };
    // Een concept van vóór de momentenkoppeling bewaarde de chart als `savedLinkedChartId`.
    const migrated = savedLinkedChartId !== undefined && draft.linkedChartId === undefined
      ? { ...draft, linkedChartId: savedLinkedChartId }
      : draft;
    // JSON kent geen `undefined` in arrays: lege plekken van de handmatige worp komen terug als null.
    return Array.isArray(migrated.manualLines)
      ? { ...migrated, manualLines: migrated.manualLines.map(value => value ?? undefined) }
      : migrated;
  } catch {
    return EMPTY_ICHING_DRAFT;
  }
}

export function saveIChingDraft(draft: IChingDraft): void {
  try {
    localStorage.setItem(ICHING_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Opslag vol of geblokkeerd (privévenster): het concept is een gemak, geen must.
  }
}

export function clearIChingDraft(): void {
  try {
    localStorage.removeItem(ICHING_DRAFT_STORAGE_KEY);
  } catch {
    // Zie saveIChingDraft.
  }
}
